<template>
  <div>
    <template v-if="toolboxGroup">
      <IconSort size="24" style="padding:10px"/>
      <p>Input Number</p>
    </template>
    <template v-else>
      <div class="set" :data-element-id="elementId" @mouseover="isHovering = true"
           @mouseleave="isHovering = false" style="max-width: 100vw">
        <div class="subset" v-if="showLabel && !labelAfter">
          <ProsemirrorEditor
              class="text"
              defaultColor="#333"
              :defaultFontName="theme.fontName"
              :editable="true"
              :viewer="viewer"
              :elementId="elementData.id"
              :value="elementText"
              :ignoreEvents="ignoreProseMirror"
              @update="value => elementData.text = value"
              @focus="proseFocus()"
              :style="getSecondaryStyle()"
          />
          <Idot :element-data="elementData"/>
        </div>
        <div class="subset-item">
          <div v-if="elementEditorType === 'time'">
            <TimeInput v-model="numberValue"
                       :elementData="elementData"
                       :placeholder="placeholderValue"
                       :time-format="timeFormat"
                       :font-size="elementData?.style?.fontSize"
                       :input-style="getStyle()"
                       :saved-time-format="savedTimeFormat"></TimeInput>
          </div>
          <div :class="{ input: !showLabel }" v-if="elementEditorType !== 'dialog' && elementEditorType !== 'time'">
            <InputNumber v-model="numberValue"
                         :inputId="elementId + '-input'"
                         :mode="mode"
                         :currency="metaCurrency"
                         :locale="metaLocale"
                         :suffix="suffix"
                         :showButtons="elementEditorType !== 'slider' && elementEditorType !== 'number'"
                         :min="getComputedMinMax ? getComputedMinMax.min : elementMin"
                         :max="getComputedMinMax ? getComputedMinMax.max: elementMax"
                         :step="elementStep"
                         :min-fraction-digits="minPrecisionValue"
                         :max-fraction-digits="precisionValue"
                         @keydown="keydown($event)"
                         @mouseover="isHovering = true"
                         @mouseout="isHovering = false"
                         @focus="focus($event)"
                         @blur="unfocus"
                         :inputStyle="getStyle()"
                         :inputProps="{ autocomplete: 'off' }"
                         :placeholder="placeholder"
                         class="model-number-input"
            />
            <Idot v-if="!showLabel" :element-data="elementData"/>
          </div>
          <div v-if="elementEditorType === 'slider'" style="padding-top: 20px;">
            <Slider v-model="elementValue"
                    :step="elementStep"
                    :max="getComputedMinMax ? getComputedMinMax.max: elementMax"
                    :min="getComputedMinMax ? getComputedMinMax.max: elementMin"
            />
          </div>

          <div v-if="elementEditorType === 'dialog'" :style="getNumberStyle()">
            <div class="vmValue" @mouseover="hover = true" @mouseleave="hover = false">
              <div class="editValue">
                <div @click.stop="dataEditVisible = !dataEditVisible">
                  {{ formattedComputedValue }}{{ suffix }}
                  <IconEdit :style="{ visibility: hover ? 'visible' : 'hidden' }" />
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="elementFieldType === 'none'" :data-element-id="elementId">
          </div>
        </div>
        <div class="subset" v-if="showLabel && labelAfter">
          <ProsemirrorEditor
              :elementId="elementId"
              class="text"
              defaultColor="#333"
              :defaultFontName="theme.fontName"
              :editable="true"
              :viewer="viewer"
              :value="elementText"
              v-if="elementText !== trailingBreak"
              @mouseover="isHovering = true"
              @mouseleave="isHovering = false"
              @update="value => elementData.text = value"
              :style="getSecondaryStyle()"
          />
          <Idot :element-data="elementData"/>
        </div>
      </div>

    </template>
  </div>

</template>

<script setup lang="ts">
import TimeInput from './TimeInput.vue'
import 'primevue/resources/themes/saga-blue/theme.css'
import 'primevue/resources/primevue.min.css'
import {computed, getCurrentInstance, onMounted, ref, toRefs, watch, nextTick} from 'vue'
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue"
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue"
import {storeToRefs} from "pinia";
import {useMainStore, useWidgetStore, useGivingTreeStore, useSlidesStore} from "@/store";
import Idot from "@/components/Idot.vue";

const mainStore = useMainStore()
const givingTreeStore = useGivingTreeStore()
const slidesStore = useSlidesStore()

const {locale, currency, theme } = storeToRefs(slidesStore)
const widgetStore = useWidgetStore()
const {inputSelected} = storeToRefs(widgetStore)

const isHovering = ref(false)
const isFocused = ref(false)
const ignoreProseMirror = ref(false)
const {editModeOn} = storeToRefs(mainStore)
const dataEditVisible = ref(false)

const trailingBreak = '<p style=""><br class="ProseMirror-trailingBreak"></p>'



const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean,
    default: false
  }
})

const precisionValue = computed(() => {
  // precision is limited in display mode but not edit mode
  return isFocused.value ? 10 : getComputedPrecisionByHookId(elementHookId.value)
})
const minPrecisionValue = computed(() => {
  return getComputedPrecisionByHookId(elementHookId.value)
})


const {elementData} = toRefs(props)
const elementId = computed(() => elementData.value?.id ?? null)
const elementText = computed(() => elementData.value?.text ?? 'Label')
const elementHookId = computed(() => elementData.value?.hookId ?? '')
const elementStep = computed(() => elementData.value?.step ?? 1)
const elementStyle = computed(() => elementData.value?.style ?? {})
const elementEditorType = computed(() => elementData.value?.editorType ?? '')
const elementFieldType = computed(() => elementData.value?.fieldType ?? '')

const sliderColor = computed(() => elementData.value?.sliderColor ?? '')

const elementMin = computed(() => elementData.value?.min )
const elementMax = computed(() => elementData.value?.max )

const elementColor = computed(() => elementStyle.value?.color ?? '')
const fontSize = computed(() => elementStyle.value?.fontSize ?? '')
const fontWeight = computed(() => elementStyle.value?.fontWeight ?? '')
const fontStyle = computed(() => elementStyle.value?.fontStyle ?? '')
const textAlign = computed(() => elementData.value?.style.justifyContent !== '' ? 'right' : elementData.value?.style.textAlign)
const textDecoration = computed(() => elementStyle.value?.textDecoration ?? '')
const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
const labelMargins = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? '0 10px' : '10px 0')
const positionFlex = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? 'row' : 'column')
const showLabel = computed(() => elementData.value?.labelPosition !== 'none')
const labelAfter = computed(() => elementData.value?.labelPosition === 'bottom' || elementData.value?.labelPosition === 'right')

const spinnerVisible = computed(() => isFocused.value || isHovering.value ? 'visible' : 'hidden')
const metaCurrency = computed(() => {
  return getComputedTextByHookId('meta-currency') ?? 'USD'
})
const metaLocale = computed(() => locale.value ?? 'en-US')

const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')
const tileAlign = computed(() => elementData.value?.style.align ?? 'center')
const inputAlign = computed(() => elementData.value?.style.inputAlign ?? 'center')

const hover = ref(false)

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const {
  getActiveHookId,
  getComputedValue,
  getOverrideValue,
  getComputedType,
  getComputedText,
  getComputedPrecision,
  getComputedPrecisionByHookId,
  getComputedMinMax,
  getComputedTextByHookId
} = useComputedTreeValue()

const mode = computed(() => {
  if (getComputedType.value === 'currency') {
    return 'currency'
  }
  return 'decimal'
})

const suffix = computed(() => {
  if (getComputedType.value === 'percent') {
    return '%'
  }
})

onMounted(() => {
  if (elementHookId.value) {
    getActiveHookId(elementHookId.value)
  }

  if (elementData.value.overrideValue !== undefined && elementData.value.overrideValue !== null && elementHookId.value) {
    // only set the override value from the control if the user hasn't set it in the model
    getActiveHookId(elementHookId.value);
    if (getOverrideValue.value === undefined || getOverrideValue.value === null) {
      getComputedValue.value = elementData.value.overrideValue;
    }
  }
})

const timeFormat = computed(() => elementData.value?.timeFormat ?? 'HH:mm ss')
const savedTimeFormat = computed(() => elementData.value?.saveTimeAs ?? 'Minutes')

const defaultLabelWidth = computed(() => {

  const parentElement = document.querySelector('.item-container');

  return parentElement.clientWidth + 'px';
})


watch(elementHookId, async (newVal) => {
  if (newVal) {
    getActiveHookId(newVal)
  }
}, {deep: true})


const formattedComputedValue = computed(() => {
  const hasComputedValue = getComputedValue.value !== null;

  if (hasComputedValue && mode.value === 'currency') {
    return currencyFormat();
  }

  if (hasComputedValue) {
    const precision = getComputedPrecision.value || 0;
    return getComputedValue.value.toFixed(precision);
  }

  return '';
})

const elementValue = computed({
  get() {
    if (elementHookId.value) {
      getActiveHookId(elementHookId.value);
      return getComputedValue.value;
    }

    return 0;
  },
  set(val) {
    // Sometimes the input number will set the formatted value
    if (isNaN(val)) {
      return
    }
    if (elementHookId.value) {
      getActiveHookId(elementHookId.value);
      getComputedValue.value = val;
    }
  }
});

watch(() => elementData.value.overrideValue, (newVal) => {
  if (newVal !== undefined && newVal !== null && elementHookId.value) {
    getActiveHookId(elementHookId.value);
    getComputedValue.value = newVal;
  }
});




const currencyFormat = () => {

  const hasCompactElementData = elementData.value?.compact;
  const value = getComputedValue.value ? getComputedValue.value : 0

  const notation = value >= 100000 && hasCompactElementData ? 'compact' : 'standard';

  return new Intl.NumberFormat(metaLocale.value, {
    notation: notation,
    compactDisplay: "long",
    minimumFractionDigits: minPrecisionValue.value,
    maximumFractionDigits: minPrecisionValue.value,
    style: mode.value,
    currency: metaCurrency.value
  }).format(value);
}

const lockStyle = (locked: boolean) => {
  if (elementData && elementData.value) {
    elementData.value.style.locked = locked
  }
}

const proseFocus = () => {
  ignoreProseMirror.value = false
  lockStyle(true)
}

const keydown = (event) => {

  const input = event.target;
  const isAllTextSelected = input.selectionStart === 0 && input.selectionEnd === input.value.length;

  if (event.key === 'ArrowLeft' && isAllTextSelected) {
    const hasCurrencySymbol = /^\p{Sc}/u.test(input.value);
    const cursorPosition = hasCurrencySymbol ? 1 : 0;
    input.setSelectionRange(cursorPosition, cursorPosition);
    event.stopPropagation();
  } else if (event.key === 'ArrowRight' && isAllTextSelected) {
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition + (event.key === 'ArrowRight' ? 1 : -1));
    event.stopPropagation();
  }

  if (event.key === '.' || event.key === ',') {
    if (isAllTextSelected) {
      input.value = 0; // This line might need adjustment depending on desired behavior
    }

    const cursorPosition = input.selectionStart;
    const nextInstancePosition = input.value.indexOf(event.key, cursorPosition);

    if (nextInstancePosition > -1) {
      const newCursorPosition = nextInstancePosition + 1;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }
};

const focus = (event) => {
  const input = event.target
  nextTick(() => {
    input.select()
  })

  isFocused.value = true
  ignoreProseMirror.value = true
  lockStyle(false)
  inputSelected.value = true


}

const unfocus = (event) => {
  elementValue.value = event.value

  isFocused.value = false
  setTimeout(() => {
    if (!document.activeElement?.classList.contains('preserve-select-input')) {
      inputSelected.value = false
    }
  }, 100)
}

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  } else {
    return defaultValue
  }
}

const getStyle = () => {
  return {
    fontFamily: theme.value.fontName ?? '',
    maxWidth: '100%',
    width: px(elementData.value.style?.width, ''),
    height: px(elementData.value.style?.height, ''),
  }
}

const getNumberStyle = () => {
  return {
    fontSize: elementData.value?.style?.fontSize,
    fontWeight: elementData.value?.style?.fontWeight,
    fontStyle: elementData.value?.style?.fontStyle,
    textDecoration: elementData.value?.style?.textDecoration,
    color: elementData.value?.style?.color,
    textAlign: elementData.value?.style?.inputAlign,
    display: 'flex',
    lineHeight: elementData.value?.style?.lineHeight ? elementData.value?.style?.lineHeight : 'normal',
    justifyContent: elementData.value?.style?.justifyContent !== '' ? elementData.value?.style?.justifyContent : elementData.value?.style?.inputAlign,
    alignItems: elementData.value?.style?.valign,
    width: px(elementData.value.style?.width, ''),
    height: px(elementData.value.style?.height, ''),
  }
}

const getSecondaryStyle = () => {
  return {
    minWidth: '30px',
    maxWidth: '100%',
    width: px(elementData.value.style?.secondaryWidth, ''),
    height: px(elementData.value.style?.secondaryHeight, ''),
  }
}

const numberValue = computed({
  get() {
    return elementData.value?.placeholder ? getOverrideValue.value : elementValue.value
  },
  set(val) {
    elementValue.value = val
  }
})


const placeholderValue = computed(() => {
  if (elementData.value.placeholder) {
    return getComputedValue.value ?? 0
  }
})

const placeholder = computed(() => {
  if (elementData.value.placeholder) {
    return currencyFormat()
  }
})

const placeholderColor = computed(() => elementStyle.value?.placeholderColor ?? '')


</script>
<style lang="scss" scoped>
:deep(.p-inputnumber-button-group) {
  visibility: visible;
}

:deep(.p-inputnumber .p-inputtext) {
  background-color: transparent;
  color: v-bind(elementColor);
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
  font-family: v-bind(fontName);
}

:deep(.p-inputtext) {
  background-color: transparent;
  color: v-bind(elementColor);
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
}

:deep(.editable-time) {
  background-color: transparent;
  color: v-bind(elementColor);
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
}

:deep(.timePlaceholder) {
  background-color: transparent;
  color: v-bind(placeholderColor);
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
}

:deep(.p-button) {
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

.set {
  display: flex;
  flex-direction: v-bind(positionFlex);
  align-items: v-bind(tileAlign);
  justify-content: v-bind(justifyContent);
}

:deep(.label) {
  padding-bottom: 5px;
}

:deep( .p-slider .p-slider-range ) {
  background: v-bind(sliderColor)
}

:deep( .p-slider-handle, .p-slider-handle:hover ) {
  border-color: v-bind(sliderColor);
  background: v-bind(sliderColor);
}

.subset {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
}


:deep(p) {
  margin: v-bind(labelMargins);
}


.input {
  display: flex;
  flex-direction: row;
}

:deep(.model-number-input input::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 1;
}

:deep(.model-number-input input:hover::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.8;
}

:deep(.model-number-input input:focus::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.5;
}

</style>
